import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { calcolaCostoIngrediente, labelUdm, formatGiacenza, udmKind, udmStorageFactor } from "../../utils/udmUtils";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "../ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ToggleSwitch as Switch } from "../ui/toggle-switch";
import {
  ChevronLeft, Plus, X, Calculator, Scale, Flame, Package, Droplets,
  ChefHat, Link2, Search, Unlink, Sparkles, ClipboardList, Timer, GripVertical
} from "lucide-react";

// Drawer per modificare dettagli singolo ingrediente
const IngredienteDetailDrawer = ({ open, onOpenChange, ing, idx, onUpdateField, onRemove, isLightRecipe, hasIngredienti, ingredientiDisponibili, onCollega, onScollega }) => {
  const [searchCollega, setSearchCollega] = useState("");
  const isNonCollegato = isLightRecipe && !ing?.ingrediente_id && !ing?.is_acqua && ing?.tipo !== "ricetta";
  const isCollegato = isLightRecipe && !!ing?.ingrediente_id;
  // Unità di misura per il display dell'input — fonte di verità: l'UDM salvata sul PRODOTTO.
  // - acqua (flag is_acqua OR nome contiene "acqua") → liquido (ml/L)
  // - altrimenti → classifica l'UDM del prodotto in peso (g) / liquido (ml) / pezzi
  const ingredienteDb = ingredientiDisponibili?.find(i => i.id === ing?.ingrediente_id);
  const prodUdm = ingredienteDb?.unita_misura || ing?.unita_misura || "g";
  const isAcqua = !!ing?.is_acqua || /\bacqua\b/i.test(ing?.ingrediente_nome || "");
  const kind = isAcqua ? "liquido" : udmKind(prodUdm);
  const isLiquido = kind === "liquido";
  const isPezzi = kind === "pezzi";
  // OPZIONE (b): l'input segue l'UDM del PRODOTTO (kg → si inserisce in kg).
  // Lo storage interno resta in unità piccola (g/ml/pz) per il calcolo costi.
  const inputLabel = isAcqua ? "ml" : labelUdm(prodUdm);
  const storageFactor = isAcqua ? 1 : udmStorageFactor(prodUdm);
  const percScarto = ing?.percentuale_scarto || 0;

  // Conversione storage(g/ml) ↔ display(UDM prodotto), con stato locale per input fluido
  const toDisplay = (g) => {
    const v = parseFloat(g);
    if (isNaN(v)) return "";
    return String(+(v / storageFactor).toFixed(4));
  };
  const [lordoStr, setLordoStr] = useState("");
  const [nettoStr, setNettoStr] = useState("");
  useEffect(() => {
    if (open) {
      setLordoStr(ing?.peso_lordo != null && ing?.peso_lordo !== "" ? toDisplay(ing.peso_lordo) : "");
      setNettoStr(ing?.quantita != null && ing?.quantita !== "" ? toDisplay(ing.quantita) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  const handleLordoChange = (val) => {
    setLordoStr(val);
    const grams = val === "" ? "" : String(+(parseFloat(val) * storageFactor).toFixed(4));
    onUpdateField(idx, "peso_lordo", grams);
  };
  const handleNettoChange = (val) => {
    setNettoStr(val);
    const grams = val === "" ? "" : String(+(parseFloat(val) * storageFactor).toFixed(4));
    onUpdateField(idx, "quantita", grams);
  };

  const filteredIngredienti = ingredientiDisponibili?.filter(i =>
    i.nome.toLowerCase().includes(searchCollega.toLowerCase())
  ).slice(0, 10) || [];

  if (!ing) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]" data-testid="ingredient-detail-drawer">
        <DrawerHeader className="text-left">
          <DrawerTitle className="flex items-center gap-2">
            {ing.is_acqua ? <Droplets className="h-5 w-5 text-blue-400" /> :
              ing.tipo === "ricetta" ? <ChefHat className="h-5 w-5 text-purple-400" /> :
                <Package className="h-5 w-5 text-lime-400" />}
            {ing.ingrediente_nome}
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-5 overflow-y-auto">
          {/* Peso/Quantità: label sempre nell'unità "piccola" (g/ml/pz) perché è come gli operatori
              sono abituati a inserire i dati. Sotto, un preview live mostra la conversione in kg/L. */}
          <div className="space-y-3">
            <Label className="text-muted-foreground text-xs uppercase tracking-wider">
              {isPezzi ? `Quantità (${inputLabel})` : `Peso (${inputLabel})`}
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">Lordo</span>
                <Input
                  type="number" inputMode="decimal" min="0"
                  value={lordoStr}
                  onChange={(e) => handleLordoChange(e.target.value)}
                  className="h-12 text-lg font-medium"
                  data-testid="detail-peso-lordo"
                />
                {ing.peso_lordo && parseFloat(ing.peso_lordo) > 0 && !isPezzi && (
                  <p className="text-[11px] text-indigo-500 font-semibold">
                    = {formatGiacenza(ing.peso_lordo, isLiquido ? "ml" : "g")}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">
                  Netto {percScarto > 0 && <span className="text-amber-400 ml-1">(-{percScarto}%)</span>}
                </span>
                <Input
                  type="number" inputMode="decimal" min="0"
                  value={nettoStr}
                  onChange={(e) => handleNettoChange(e.target.value)}
                  className="h-12 text-lg font-medium"
                  data-testid="detail-peso-netto"
                />
                {ing.quantita && parseFloat(ing.quantita) > 0 && !isPezzi && (
                  <p className="text-[11px] text-indigo-500 font-semibold">
                    = {formatGiacenza(ing.quantita, isLiquido ? "ml" : "g")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Cottura */}
          {!ing.is_acqua && ing.tipo !== "ricetta" && (
            <div className="space-y-3 p-4 rounded-xl border border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-400" />
                  <span className="font-medium">Cottura</span>
                </div>
                <Switch
                  checked={ing.cotto || false}
                  onCheckedChange={(v) => onUpdateField(idx, "cotto", v)}
                  data-testid="detail-cotto-switch"
                />
              </div>
              {ing.cotto && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Calo cottura (%)</Label>
                  <Input
                    type="number" min="0" max="100" inputMode="numeric"
                    value={ing.calo_cottura_percentuale || ""}
                    onChange={(e) => onUpdateField(idx, "calo_cottura_percentuale", e.target.value)}
                    className="h-12 text-lg"
                    data-testid="detail-calo-cottura"
                  />
                </div>
              )}
            </div>
          )}

          {/* Collega / Scollega per ricette light */}
          {isNonCollegato && hasIngredienti && (
            <div className="space-y-3 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
              <div className="flex items-center gap-2 text-amber-400">
                <Link2 className="h-4 w-4" />
                <span className="font-medium text-sm">Collega a ingrediente anagrafica</span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchCollega} onChange={(e) => setSearchCollega(e.target.value)}
                  placeholder="Cerca ingrediente..."
                  className="pl-10 h-12"
                  data-testid="detail-collega-search"
                />
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filteredIngredienti.map(ingDb => (
                  <button key={ingDb.id} type="button" onClick={() => { onCollega(idx, ingDb); onOpenChange(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left hover:bg-primary/10 transition-colors"
                    data-testid={`detail-collega-${ingDb.id}`}>
                    <Package className="h-4 w-4 text-lime-400 flex-shrink-0" />
                    <span className="text-sm truncate">{ingDb.nome}</span>
                    {ingDb.prezzo_attuale > 0 && <span className="text-xs text-muted-foreground ml-auto">{ingDb.prezzo_attuale.toFixed(2)}/kg</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {isCollegato && (
            <Button type="button" variant="outline" onClick={() => { onScollega(idx); }}
              className="w-full h-12 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
              data-testid="detail-scollega-btn">
              <Unlink className="h-4 w-4 mr-2" />Scollega ingrediente
            </Button>
          )}
        </div>

        <DrawerFooter className="flex-row gap-3 border-t">
          <Button type="button" variant="outline" onClick={() => { onRemove(idx); onOpenChange(false); }}
            className="text-red-400 border-red-500/30 hover:bg-red-500/10"
            data-testid="detail-remove-btn">
            <X className="h-4 w-4 mr-1" />Rimuovi
          </Button>
          <Button type="button" className="flex-1 h-12 font-bold" onClick={() => onOpenChange(false)}
            data-testid="detail-done-btn">
            Fatto
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export const ComposizioneDialog = ({
  open, onOpenChange, editItem, form, onFormChange, saving,
  categorie, ingredientiDisponibili, ricette, isAdmin,
  onSubmit, onAddIngrediente, onRemoveIngrediente, onUpdateField,
  onMoveUp, onMoveDown, costi, formatTempo
}) => {
  const [detailIdx, setDetailIdx] = useState(null);
  const dialogLockRef = useRef(false);
  const isLightRecipe = editItem?.is_light === true;
  const hasIngredienti = ingredientiDisponibili.length > 0;
  const nonCollegati = form.ingredienti.filter(i => !i.ingrediente_id && !i.is_acqua && i.tipo !== "ricetta").length;
  const tuttiCollegati = isLightRecipe && nonCollegati === 0 && form.ingredienti.length > 0;

  const handleCollega = (idx, ingredienteDb) => {
    onFormChange(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.map((ing, i) => i === idx ? {
        ...ing, ingrediente_id: ingredienteDb.id, ingrediente_nome: ingredienteDb.nome, prezzo_usato: ingredienteDb.prezzo_attuale || 0
      } : ing)
    }));
  };

  const handleScollega = (idx) => {
    onFormChange(prev => ({
      ...prev,
      ingredienti: prev.ingredienti.map((ing, i) => i === idx ? { ...ing, ingrediente_id: "", prezzo_usato: 0 } : ing)
    }));
  };

  const detailIng = detailIdx !== null ? form.ingredienti[detailIdx] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && dialogLockRef.current) return; setDetailIdx(null); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 sm:rounded-3xl sm:max-h-[90vh] max-h-dvh h-dvh sm:h-auto w-full sm:w-auto rounded-none border-0 sm:border overflow-hidden flex flex-col" data-testid="composizione-dialog">
        <DialogTitle className="sr-only">{editItem ? "Modifica Ricetta" : "Nuova Ricetta"}</DialogTitle>
        {/* Header fisso */}
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <Button type="button" variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-9 w-9 flex-shrink-0" data-testid="composizione-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-base truncate">{editItem ? "Modifica Ricetta" : "Nuova Ricetta"}</h2>
            {isLightRecipe && <span className="text-[10px] font-bold text-amber-500">LIGHT</span>}
          </div>
        </div>

        {/* Contenuto scrollabile */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-5 pb-32">
            {/* Banner Light */}
            {isLightRecipe && hasIngredienti && (
              <div className={`p-3 rounded-xl border text-sm ${tuttiCollegati ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                {tuttiCollegati ? (
                  <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-500 flex-shrink-0" /><span className="text-emerald-500 font-medium text-xs">Tutti collegati! Puoi convertire in completa.</span></div>
                ) : (
                  <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-amber-500 flex-shrink-0" /><span className="text-amber-500 font-medium text-xs">Collega gli ingredienti ({nonCollegati} da collegare)</span></div>
                )}
              </div>
            )}

            {/* Info Ricetta */}
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Nome *</Label>
                <Input value={form.nome} onChange={(e) => onFormChange(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="es. Salsa Quattro Formaggi" className="h-12 text-base font-medium" data-testid="ricetta-nome-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Categoria</Label>
                  <Select value={form.categoria_id} onValueChange={(v) => onFormChange(prev => ({ ...prev, categoria_id: v }))}>
                    <SelectTrigger className="h-12"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{categorie.map(c => (<SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Timer className="h-3 w-3" />Tempo (min)</Label>
                  <Input type="number" min="1" value={form.tempo_preparazione} onChange={(e) => onFormChange(prev => ({ ...prev, tempo_preparazione: e.target.value }))}
                    placeholder="45" className="h-12" data-testid="ricetta-tempo-input" />
                </div>
              </div>
            </div>

            {/* Toggle tracciabilità compatto */}
            <div className="flex items-center justify-between p-3 rounded-xl border border-border">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Tracciabilità Lotti</span>
              </div>
              <Switch checked={form.richiede_tracciabilita || false} onCheckedChange={(checked) => onFormChange(prev => ({ ...prev, richiede_tracciabilita: checked }))} />
            </div>

            {/* Ingredienti */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Ingredienti ({form.ingredienti.length})</Label>
                <Button type="button" size="sm" onClick={onAddIngrediente} className="h-9 gap-1.5 font-bold" data-testid="add-ingrediente-btn">
                  <Plus className="h-4 w-4" />Aggiungi
                </Button>
              </div>

              {form.ingredienti.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-xl">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessun ingrediente</p>
                  <p className="text-xs mt-1">Tocca &quot;Aggiungi&quot; per iniziare</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {form.ingredienti.map((ing, idx) => {
                    const isSottoRicetta = ing.tipo === "ricetta" && ing.ricetta_id;
                    const ingredienteDb = ingredientiDisponibili.find(i => i.id === ing.ingrediente_id);
                    const ricettaDb = isSottoRicetta ? ricette.find(r => r.id === ing.ricetta_id) : null;
                    const prezzoBase = isSottoRicetta ? (ricettaDb?.costo_per_kg || ing.prezzo_usato || 0) : (ingredienteDb?.prezzo_attuale || ing.prezzo_usato || 0);
                    const quantitaNum = parseFloat(ing.quantita) || 0;
                    const pesoLordoNum = parseFloat(ing.peso_lordo) || quantitaNum;
                    // Fallback intelligente per acqua: se is_acqua=true O nome contiene "acqua" → liquido
                    const isAcquaRow = !!ing.is_acqua || /\bacqua\b/i.test(ing.ingrediente_nome || "");
                    // Usa sempre la UDM corrente dal DB (se disponibile), forza ml per acqua
                    const udm = isAcquaRow ? "ml" : (ingredienteDb?.unita_misura || ing.unita_misura || "g");
                    const costo = isAcquaRow ? 0 : calcolaCostoIngrediente(pesoLordoNum, prezzoBase, udm);
                    const isNonCollegato = isLightRecipe && !ing.ingrediente_id && !isAcquaRow && ing.tipo !== "ricetta";

                    return (
                      <button
                        key={idx} type="button"
                        onClick={() => setDetailIdx(idx)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left active:scale-[0.98] ${isNonCollegato ? "border-amber-500/30 bg-amber-500/5" : "border-border hover:border-primary/30"}`}
                        data-testid={`ingrediente-row-${idx}`}
                      >
                        {/* Icona tipo */}
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isAcquaRow ? 'bg-blue-500/20' : isSottoRicetta ? 'bg-purple-500/20' : isNonCollegato ? 'bg-amber-500/20' : 'bg-lime-400/20'}`}>
                          {isAcquaRow ? <Droplets className="h-4 w-4 text-blue-400" /> :
                            isSottoRicetta ? <ChefHat className="h-4 w-4 text-purple-400" /> :
                              isNonCollegato ? <Unlink className="h-4 w-4 text-amber-400" /> :
                                <Package className="h-4 w-4 text-lime-400" />}
                        </div>

                        {/* Nome e badges — quantità formattata in unità leggibile (15000g → "15 kg") */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{ing.ingrediente_nome}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-muted-foreground">{formatGiacenza(quantitaNum, udm)}</span>
                            {ing.cotto && <span className="text-[10px] text-orange-400"><Flame className="h-3 w-3 inline" /></span>}
                            {isSottoRicetta && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400">RIC</span>}
                          </div>
                        </div>

                        {/* Costo */}
                        {isAdmin && ing.ingrediente_id && !isAcquaRow && (
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-sm text-lime-400">{costo.toFixed(2)}</p>
                          </div>
                        )}

                        {/* Indicatore non collegato */}
                        {isNonCollegato && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 flex-shrink-0">LINK</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Calcolo costi compatto */}
            {isAdmin && form.ingredienti.length > 0 && (() => {
              const pesoManuale = form.peso_finale_kg !== "" && form.peso_finale_kg != null ? parseFloat(form.peso_finale_kg) : null;
              const costoTotNum = parseFloat(costi.costoTotale) || 0;
              const costoKgEff = (pesoManuale && pesoManuale > 0) ? (costoTotNum / pesoManuale).toFixed(2) : costi.costoPerKg;
              return (
              <div className="p-4 rounded-xl bg-muted/30 border border-border space-y-4">
                <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-lime-400" /><span className="font-medium text-sm">Costi</span></div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div><p className="text-[10px] text-muted-foreground uppercase">Peso</p><p className="font-bold">{pesoManuale && pesoManuale > 0 ? `${Math.round(pesoManuale * 1000)}g` : `${costi.pesoFinaleGrammi}g`}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase">Totale</p><p className="font-bold">{costi.costoTotale}</p></div>
                  <div><p className="text-[10px] text-muted-foreground uppercase">/Kg</p><p className="font-bold text-lime-400">{costoKgEff}</p></div>
                </div>
                {/* Peso reale dopo cottura (override manuale) */}
                <div className="pt-3 border-t border-border space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Scale className="h-3 w-3" />Peso dopo cottura (kg)</Label>
                  <Input
                    type="number" inputMode="decimal" min="0" step="0.001"
                    value={form.peso_finale_kg ?? ""}
                    onChange={(e) => onFormChange(prev => ({ ...prev, peso_finale_kg: e.target.value }))}
                    placeholder={`Calcolato: ${costi.pesoFinaleKg} kg`}
                    className="h-11"
                    data-testid="ricetta-peso-finale-input"
                  />
                  <p className="text-[11px] text-muted-foreground">Inserisci il peso reale della ricetta dopo la cottura per ottenere il food cost/kg corretto. Lascia vuoto per usare il peso calcolato dagli ingredienti.</p>
                </div>
              </div>
              );
            })()}

            {/* Converti completa */}
            {isLightRecipe && tuttiCollegati && (
              <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30" data-testid="converti-completa-section">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-emerald-400 font-medium text-sm">Pronta per la conversione</p>
                    <p className="text-xs text-muted-foreground">Tutti collegati. Abilita calcolo costi.</p>
                  </div>
                </div>
                <Button type="button" size="sm" onClick={() => onFormChange(prev => ({ ...prev, is_light: false }))}
                  className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="converti-completa-btn">
                  Converti in Completa
                </Button>
              </div>
            )}

            {/* Note */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Note</Label>
              <Input value={form.note} onChange={(e) => onFormChange(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Istruzioni, note..." className="h-12" />
            </div>
          </div>
        </form>

        {/* Footer fisso */}
        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3 flex-shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-12 font-medium" data-testid="composizione-cancel-btn">Annulla</Button>
          <Button type="button" onClick={(e) => { e.preventDefault(); onSubmit(e); }} disabled={saving} className="flex-1 h-12 font-bold" data-testid="composizione-save-btn">
            {saving ? "Salvataggio..." : (editItem ? "Salva" : "Crea")}
          </Button>
        </div>
      </DialogContent>

      {/* Drawer dettaglio ingrediente */}
      <IngredienteDetailDrawer
        open={detailIdx !== null}
        onOpenChange={(v) => {
          if (!v) {
            dialogLockRef.current = true;
            setTimeout(() => { dialogLockRef.current = false; }, 350);
            setDetailIdx(null);
          }
        }}
        ing={detailIng}
        idx={detailIdx}
        onUpdateField={onUpdateField}
        onRemove={onRemoveIngrediente}
        isLightRecipe={isLightRecipe}
        hasIngredienti={hasIngredienti}
        ingredientiDisponibili={ingredientiDisponibili}
        onCollega={handleCollega}
        onScollega={handleScollega}
      />
    </Dialog>
  );
};
